import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { BigNumber, Button, FormattedNumber } from '../../../../components'
import { endOfMonth, startOfMonth, subMonths } from 'date-fns'
import { useCreditsEndOfMonthForecast, useWorkspaceCreditsAvailable, useWorkspaceCreditsUsage } from '../../../../hooks'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export function CreditsSection () {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const params = useParams<{ workspace: string }>()
  const workspace = params.workspace!

  const {
    data: workspaceCreditsAvailable,
    isError: errorWorkspaceAvailable,
    isLoading: loadingWorkspaceAvailable,
    refetch: refetchWorkspaceCreditsUsage
  } = useWorkspaceCreditsAvailable(workspace)

  const {
    data: lastMonthCredits,
    isError: errorCogfyCreditsUsageLastMonth,
    isLoading: loadingCogfyCreditsUsageLastMonth,
    refetch: refetchCogfyCreditsUsageLastMonth
  } = useWorkspaceCreditsUsage(workspace, {
    startDate: startOfMonth(subMonths(new Date(), 1)),
    endDate: endOfMonth(subMonths(new Date(), 1))
  })

  const {
    data: creditsForecast,
    isError: errorCreditsForecast,
    isLoading: loadingCreditsForecast,
    refetch: refetchCreditsForecast
  } = useCreditsEndOfMonthForecast(workspace)

  const creditsUsageGrowthPercentage = lastMonthCredits
    ? 100 * ((creditsForecast ?? 0) - lastMonthCredits) / lastMonthCredits
    : 0

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex w-full justify-between'>
        <h2 className="text-2xl font-semibold">Credits</h2>
        <Button
          className='w-fit bg-transparent h-auto px-3 border-primary text-primary hover:bg-primary hover:text-white'
          onClick={() => navigate('../credits')}
        >
          {t('Details')}
        </Button>
      </div>

      <div className="flex flex-row gap-4">
        <BigNumber.Card
          loading={loadingWorkspaceAvailable || loadingCogfyCreditsUsageLastMonth}
          error={errorWorkspaceAvailable || errorCogfyCreditsUsageLastMonth}
          onRefetchClick={() => {
            refetchWorkspaceCreditsUsage()
            refetchCogfyCreditsUsageLastMonth()
          }}
        >
          <span>{t('workspaceUsagePage:You currently have')}</span>
          <BigNumber.Number>
            <FormattedNumber value={workspaceCreditsAvailable || 0}/>
          </BigNumber.Number>
          <BigNumber.AdditionalInfo>
            {t('workspaceUsagePage:Last month') + ': '}
            <FormattedNumber value={lastMonthCredits || 0} className="font-bold" />
          </BigNumber.AdditionalInfo>
        </BigNumber.Card>

        <BigNumber.Card
          loading={loadingCreditsForecast}
          error={errorCreditsForecast}
          onRefetchClick={() => { refetchCreditsForecast() }}
        >
          <span>{t('workspaceUsagePage:Credits usage forecast for this month')}</span>
          <BigNumber.Number>
            <FormattedNumber value={creditsForecast || 0} compact />
            <div className="flex flex-row items-end">
              <span
                className="tooltip tooltip-top pl-1 before:w-[200px] before:content-[attr(data-tip)] font-normal"
                data-tip="Em relação ao mês anterior"
              >
                <div className="flex flex-row mb-0.75">
                  {creditsUsageGrowthPercentage < 0 && <ArrowDownRight size={20} className='text-gray-400' />}
                  {creditsUsageGrowthPercentage > 0 && <ArrowUpRight size={20} className='text-gray-400' />}
                  {creditsUsageGrowthPercentage !== 0 && (
                    <>
                      <FormattedNumber className="text-sm text-light text-gray-400" value={Math.abs(creditsUsageGrowthPercentage)} compact/>
                      <span className="text-sm text-light text-gray-400">%</span>
                    </>
                  )}
                </div>
              </span>
            </div>
          </BigNumber.Number>
        </BigNumber.Card>
      </div>
    </div>
  )
}
